/**
 * Outcomes analytics API service (Phase 4.2).
 *
 * Tenant is taken from the global `x-tenant-id` header (set by the axios
 * interceptor). The agent filter and date range are passed as query params so
 * they never disturb the global agent context.
 *
 * Pure async functions — no React state, no hooks.
 */
import { get } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

// ── Domain types (mirror the backend `OutcomesAnalytics` payload) ──────────────

export type ConversationLifecycleStatus =
  | 'active'
  | 'awaiting_user'
  | 'escalated'
  | 'resolved'
  | 'abandoned';

/** Origin channel a conversation/question came through (V — visibility). */
export type ConversationChannel = 'web' | 'voice' | 'phone';

export type ConversationPhase =
  | 'exploring'
  | 'answering'
  | 'capturing_lead'
  | 'discovery'
  | 'bug_triage'
  | 'handoff'
  | 'closing';

export interface StatusCount {
  status: ConversationLifecycleStatus;
  conversations: number;
}

/** Conversations grouped by origin channel (V3.3 — web vs voice). */
export interface ChannelCount {
  channel: ConversationChannel;
  conversations: number;
}

/** Voice call volume/cost rollup (V3.3), scoped like the rest of this payload. */
export interface VoiceUsage {
  /** Total Vapi calls in scope. */
  calls: number;
  /** Summed call duration, in minutes (rounded to 1 decimal place). */
  totalMinutes: number;
  /** Summed Vapi call cost (the tenant's own Vapi billing units). */
  totalCost: number;
}

export interface PhaseCount {
  phase: ConversationPhase;
  conversations: number;
}

export interface ToolUsageCount {
  tool: string;
  total: number;
  succeeded: number;
  failed: number;
}

export interface FeedbackMetrics {
  responses: number;
  positive: number;
  negative: number;
  satisfactionRate: number;
}

export interface OutcomesAnalytics {
  range: { from: string | null; to: string | null };
  agentId: string | null;
  conversations: {
    handled: number;
    escalated: number;
    /** Share handled without escalation, `0..1`. */
    deflectionRate: number;
    byStatus: StatusCount[];
    /** Channel split — every channel, in canonical order, zero-filled (V3.3). */
    byChannel: ChannelCount[];
  };
  outcomes: {
    leads: number;
    requirementBriefs: number;
    ticketsFiled: number;
  };
  phaseFunnel: PhaseCount[];
  topIntents: PhaseCount[];
  toolUsage: ToolUsageCount[];
  feedback: FeedbackMetrics;
  /** Voice call volume/cost, for per-channel ROI (V3.3). */
  voice: VoiceUsage;
}

export interface OutcomesQuery {
  agentId?: string | null;
  from?: string | null;
  to?: string | null;
}

// ── Knowledge gaps (mirror the backend `KnowledgeGaps` payload, Phase 4.3) ─────

/** Per-cluster occurrence split by originating channel (V — visibility). */
export interface KnowledgeGapChannelBreakdown {
  web: number;
  voice: number;
  /** Orlenn phone calls (optional: older API responses omit it). */
  phone?: number;
}

/** A group of near-identical questions the knowledge base couldn't answer well. */
export interface KnowledgeGapCluster {
  key: string;
  /** Most representative phrasing of the question. */
  question: string;
  /** A few example phrasings, most frequent first. */
  samples: string[];
  questions?: KnowledgeGapQuestionInstance[];
  /** How many gap searches fell into this cluster. */
  occurrences: number;
  /** Distinct conversations affected. */
  conversations: number;
  /** Closest relevance the KB ever reached, `0..1`; null when never any match. */
  bestScore: number | null;
  /** ISO timestamp of the most recent occurrence. */
  lastSeen: string;
  /** Occurrence split by originating channel (V — visibility). */
  channelBreakdown: KnowledgeGapChannelBreakdown;
}

/** One exact ask that rolled into a cluster. */
/** How an answer was produced this turn (operator quality view). */
export type AnswerMethod =
  | 'direct'
  | 'judge_rescued'
  | 'closest_match'
  | 'extractive'
  | 'safety_fallback'
  | 'refused';

export interface KnowledgeGapQuestionInstance {
  /** Exact text the visitor typed. */
  question: string;
  /** Agent `knowledge_search` query when it differs from the typed text. */
  searchQuery?: string | null;
  sessionId: string;
  createdAt: string;
  channel: ConversationChannel;
  bestScore: number | null;
  /** How the answer was produced; `null`/absent when not applicable. */
  answerMethod?: AnswerMethod | null;
}

export interface KnowledgeGaps {
  range: { from: string | null; to: string | null };
  agentId: string | null;
  /** Relevance-score threshold below which a retrieval counts as a gap. */
  threshold: number;
  totals: {
    searches: number;
    gaps: number;
    /** `gaps / searches`, `0..1`. */
    gapRate: number;
    affectedConversations: number;
    negativeFeedback: number;
    /** Voice calls treated as a negative signal (silence timeout). */
    voiceNegativeSignals?: number;
    /** Clusters with no retrieval at all. */
    noMatchClusters?: number;
    /** Clusters with weak retrieval below the threshold. */
    weakMatchClusters?: number;
  };
  clusters: KnowledgeGapCluster[];
  pagination: {
    page: number;
    limit: number;
    totalClusters: number;
    totalPages: number;
  };
}

export type GapSortBy = 'lastSeen' | 'occurrences' | 'conversations' | 'bestScore';
export type GapMatchFilter = 'all' | 'no_match' | 'weak';
export type GapChannelFilter = 'all' | 'web' | 'voice' | 'phone';

export interface KnowledgeGapsQuery {
  agentId?: string | null;
  from?: string | null;
  to?: string | null;
  /** Override the gap threshold (`0..1`). */
  threshold?: number | null;
  page?: number;
  limit?: number;
  sortBy?: GapSortBy;
  sortDir?: 'asc' | 'desc';
  channel?: GapChannelFilter;
  match?: GapMatchFilter;
}

/** Strong KB matches — inverse of KnowledgeGaps (at/above threshold). */
export interface KnowledgeAnswers {
  range: { from: string | null; to: string | null };
  agentId: string | null;
  threshold: number;
  totals: {
    searches: number;
    answered: number;
    answerRate: number;
    affectedConversations: number;
    answerClusters: number;
  };
  clusters: KnowledgeGapCluster[];
  pagination: {
    page: number;
    limit: number;
    totalClusters: number;
    totalPages: number;
  };
}

export interface KnowledgeAnswersQuery {
  agentId?: string | null;
  from?: string | null;
  to?: string | null;
  threshold?: number | null;
  page?: number;
  limit?: number;
  sortBy?: GapSortBy;
  sortDir?: 'asc' | 'desc';
  channel?: GapChannelFilter;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const analyticsService = {
  getOutcomes(query: OutcomesQuery = {}): Promise<OutcomesAnalytics> {
    const params: Record<string, string> = {};
    if (query.agentId) params.agentId = query.agentId;
    if (query.from) params.from = query.from;
    if (query.to) params.to = query.to;
    return get<OutcomesAnalytics>(endpoints.analytics.outcomes, { params });
  },

  getKnowledgeGaps(query: KnowledgeGapsQuery = {}): Promise<KnowledgeGaps> {
    const params: Record<string, string> = {};
    if (query.agentId) params.agentId = query.agentId;
    if (query.from) params.from = query.from;
    if (query.to) params.to = query.to;
    if (query.threshold != null) params.threshold = String(query.threshold);
    if (query.page != null) params.page = String(query.page);
    if (query.limit != null) params.limit = String(query.limit);
    if (query.sortBy) params.sortBy = query.sortBy;
    if (query.sortDir) params.sortDir = query.sortDir;
    if (query.channel && query.channel !== 'all') params.channel = query.channel;
    if (query.match && query.match !== 'all') params.match = query.match;
    return get<KnowledgeGaps>(endpoints.analytics.knowledgeGaps, { params });
  },

  getKnowledgeAnswers(
    query: KnowledgeAnswersQuery = {},
  ): Promise<KnowledgeAnswers> {
    const params: Record<string, string> = {};
    if (query.agentId) params.agentId = query.agentId;
    if (query.from) params.from = query.from;
    if (query.to) params.to = query.to;
    if (query.threshold != null) params.threshold = String(query.threshold);
    if (query.page != null) params.page = String(query.page);
    if (query.limit != null) params.limit = String(query.limit);
    if (query.sortBy) params.sortBy = query.sortBy;
    if (query.sortDir) params.sortDir = query.sortDir;
    if (query.channel && query.channel !== 'all') params.channel = query.channel;
    return get<KnowledgeAnswers>(endpoints.analytics.knowledgeAnswers, {
      params,
    });
  },
} as const;
